/**
 * giaoDienDanhSach.js — BỘ HÌNH DẠNG CHUNG của màn hình danh sách.
 *
 * Màn hình danh sách hiện có hai bộ vẽ: DataListManager (cấu hình `data_list_config`, route `/page/...`) và
 * InputBase (listinfo, route `/list/...`, `/voucher/...`, ô chọn bản ghi, danh sách bật nhanh). Hai bộ dùng
 * logic khác nhau nhưng người dùng phải thấy CÙNG MỘT giao diện: thanh tiêu đề, ô tìm nhanh, nút công cụ,
 * bảng, thanh phân trang, thẻ, nút thao tác của dòng. File này là nơi DUY NHẤT giữ các giá trị đó.
 *
 * THUẦN (không import gì) để test node đọc được trên chính mã nguồn. Hàm `alpha` của MUI được TIÊM vào
 * qua tham số ở những chỗ cần pha độ trong của màu chủ đạo.
 *
 * ⚠️ Đổi một giá trị ở đây là đổi cho CẢ HAI bộ. Test `inputbase-giao-dien-test` chốt việc InputBase lấy
 * hình dạng từ đây và giá trị ở đây khớp với DataListManager.
 */

/** Nền màn hình danh sách (xám nhạt kiểu iOS) — DataListManager gọi là IOS_BG. */
export const NEN_DANH_SACH = '#f2f2f7';

/** Màu nhấn (xanh iOS) của bộ lọc nhanh / dòng đang chọn. */
export const MAU_NHAN = '#007aff';

// ── THANH TIÊU ĐỀ ──────────────────────────────────────────────────────────────────────────────────────

/** Thanh tiêu đề: nền màu chủ đạo trong 90% + làm mờ phía sau, tự rớt dòng khi hẹp. */
export function sxThanhTieuDe(alpha, { cheDoBang = false } = {}) {
  return {
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.9),
    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    color: 'primary.contrastText',
    pt: 'max(12px, env(safe-area-inset-top))',
    pb: 1.5, px: 2,
    mb: cheDoBang ? 0 : 1,
    display: 'flex', alignItems: 'center',
    flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 1100,
    flexWrap: 'wrap',
    gap: 1.5,
  };
}

/** Tiêu đề màn hình trên thanh tiêu đề. */
export const sxTieuDe = { color: 'inherit', fontSize: '1.1rem' };

/**
 * Vùng nút công cụ bên phải thanh tiêu đề: mọi nút là ô viền mảnh 44×32 chữ trắng mờ.
 * Nút CÓ CHỮ (vd "Chọn", "Huỷ" của ô chọn bản ghi) đặt trong khối `.nut-co-chu` để không bị ép về 44px.
 */
export const sxVungCongCu = {
  display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
  flex: '1 1 auto',
  justifyContent: 'flex-end',
  '& .MuiIconButton-root, & .MuiButton-root': {
    borderRadius: 1,
    border: '1px solid rgba(255, 255, 255, 0.3)',
    minWidth: 44, width: 44, height: 32, padding: 0,
    color: 'rgba(255, 255, 255, 0.7)', bgcolor: 'transparent', transition: 'all 0.2s ease',
    '&:hover': { color: 'white', borderColor: 'rgba(255, 255, 255, 0.5)', bgcolor: 'rgba(255, 255, 255, 0.1)' },
    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)', borderColor: 'rgba(255, 255, 255, 0.1)' },
  },
};

/** Khối nút có chữ trong vùng công cụ: giữ ô viền mảnh nhưng rộng theo chữ. */
export const sxNutCoChu = {
  display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
  '& .MuiButton-root': { width: 'auto', px: 1.5, textTransform: 'none', whiteSpace: 'nowrap' },
};

/** Vạch ngăn giữa nhóm tìm kiếm và nhóm nút. */
export const sxVachNgan = { borderColor: 'rgba(255, 255, 255, 0.3)', mx: 2, my: 0.5 };

/** Nút "Sắp xếp" khi đang có kiểu sắp xếp được chọn. */
export const sxNutDangChon = { color: 'white !important', borderColor: 'rgba(255, 255, 255, 0.5) !important', bgcolor: 'rgba(255, 255, 255, 0.2) !important' };

/** Nút "Xoá" sáng đỏ khi rê chuột. */
export const sxNutXoaTieuDe = { '&:hover': { color: '#ff3b30 !important', borderColor: 'rgba(255, 59, 48, 0.5) !important', bgcolor: 'rgba(255, 59, 48, 0.1) !important' } };

/** Nhóm nút chuyển "Dạng bảng / Dạng lưới". */
export const sxNhomCheDoXem = {
  height: 32,
  '& .MuiToggleButton-root': {
    color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.3)', px: 1.2,
    '&.Mui-selected': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.2)', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' } },
  },
};

/** Khung menu thả xuống (sắp xếp, nhập/xuất…) mở từ thanh tiêu đề. */
export const sxKhungMenu = { borderRadius: 3, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' };

// ── Ô TÌM NHANH ────────────────────────────────────────────────────────────────────────────────────────

/** Ô tìm nhanh NẰM TRÊN thanh tiêu đề (máy tính): nền trắng trong, chữ trắng. */
export const sxOTimTrenTieuDe = {
  '& .MuiOutlinedInput-root': {
    height: 32, borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.15)', color: 'white',
    '& fieldset': { border: '1px solid rgba(255, 255, 255, 0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: 'white' },
    '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 },
  },
};
export const sxIconTimTrenTieuDe = { color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 18 };

/** Nút "Bộ lọc nâng cao" cạnh ô tìm nhanh trên thanh tiêu đề. */
export const sxNutLocTrenTieuDe = { minWidth: 44, width: 44, height: 32, p: 0, borderRadius: 1, borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.7)', bgcolor: 'transparent', '&:hover': { color: 'white', borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.1)' } };

/** Nút "Thêm mới" trên thanh tiêu đề: nền trắng, chữ màu chủ đạo. */
export const sxNutThemTrenTieuDe = { minWidth: 44, width: 44, height: 32, p: 0, borderRadius: 1, bgcolor: 'white', color: 'primary.main', boxShadow: 'none', '&:hover': { bgcolor: '#f0f0f0' } };

// ── THANH THAO TÁC DƯỚI ĐÁY (ĐIỆN THOẠI) ───────────────────────────────────────────────────────────────

/** Thanh dưới đáy màn hình điện thoại: ô tìm nhanh + bộ lọc + thêm mới. */
export const sxThanhDuoiDay = {
  display: 'flex', gap: 1.5, px: 2,
  pt: 1.5,
  pb: 'max(16px, env(safe-area-inset-bottom))',
  bgcolor: 'white',
  borderTop: '1px solid #e5e5ea',
  boxShadow: '0 -4px 20px rgba(0,0,0,0.03)',
};
export const sxOTimDuoiDay = { '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f2f2f7', '& fieldset': { border: 'none' } } };
export const sxIconTimDuoiDay = { color: '#8e8e93', mr: 1, fontSize: 20 };
export const sxNutVienDuoiDay = { minWidth: 44, width: 44, p: 0, borderRadius: '12px', borderColor: '#d1d1d6', color: MAU_NHAN, bgcolor: 'white' };
export const sxNutThemDuoiDay = { minWidth: 44, width: 44, p: 0, borderRadius: '12px', bgcolor: MAU_NHAN, color: 'white', boxShadow: 'none' };

// ── BẢNG ───────────────────────────────────────────────────────────────────────────────────────────────

export const VIEN_O = '1px solid #e0e0e0';
export const VIEN_O_TIEU_DE = '1px solid #cfd8dc';

/**
 * Khung bọc bảng: viền mảnh, bo góc, nền trắng.
 * ⚠️ Khung này đặt THẲNG lên TableContainer (InputBase không có Paper bọc ngoài như DataListManager) nên
 * PHẢI để `overflow: 'auto'` — chính nó là vùng cuộn của bảng. Đặt 'hidden' là bảng mất cuộn cả dọc lẫn
 * ngang: dòng dưới cùng và cột bên phải bị cắt mất (lỗi 22/10/2026). Bo góc vẫn cắt nội dung vì phần tử
 * có overflow khác 'visible'.
 */
export const sxKhungBang = { border: VIEN_O, borderRadius: 2, bgcolor: 'white', overflow: 'auto' };

/** Ô tiêu đề cột. */
export const sxOTieuDeCot = { fontWeight: 'bold', bgcolor: '#f8f9fa', border: VIEN_O_TIEU_DE, whiteSpace: 'nowrap' };

/** Ô tiêu đề của cột GHIM (chọn dòng / thao tác). */
export const sxOTieuDeCotGhim = { fontWeight: 'bold', bgcolor: '#e3f2fd', border: VIEN_O_TIEU_DE };

/** Ô dữ liệu. */
export const sxODuLieu = { border: VIEN_O, fontSize: '0.875rem' };

/** Nền dòng đang chọn (khung chi tiết đang hiện dòng này). */
export const NEN_DONG_DANG_CHON = '#e3f2fd';

/** Thanh phân trang dưới bảng. */
export const sxThanhPhanTrang = { flexShrink: 0, borderTop: VIEN_O, bgcolor: '#f8f9fa' };
export const NHAN_SO_DONG = 'Số dòng:';
export const nhanDongHienThi = ({ from, to, count }) => `${from} - ${to} trong tổng ${count}`;
export const SO_DONG_MOI_TRANG = [10, 20, 50, 100];

// ── NÚT THAO TÁC CỦA DÒNG ──────────────────────────────────────────────────────────────────────────────

/** Màu viên nút thao tác dòng: chữ + nền nhạt. */
export const MAU_NUT_DONG = {
  xem: { color: MAU_NHAN, bgcolor: '#e3f2fd' },
  sua: { color: '#ff9500', bgcolor: '#fff3e0' },
  saoChep: { color: '#34c759', bgcolor: '#e8f5e9' },
  xoa: { color: '#ff3b30', bgcolor: '#ffebee' },
  them: { color: MAU_NHAN, bgcolor: '#e3f2fd' },
  khac: { color: '#636366', bgcolor: '#f2f2f7' },
};
export function sxNutDong(loai) {
  const m = MAU_NUT_DONG[loai] || MAU_NUT_DONG.khac;
  return { color: m.color, bgcolor: m.bgcolor, p: 0.5, '&:hover': { bgcolor: m.bgcolor, filter: 'brightness(0.95)' } };
}
export const CO_ICON_NUT_DONG = 16;

// ── THẺ (DẠNG LƯỚI) ────────────────────────────────────────────────────────────────────────────────────

export const sxThe = {
  position: 'relative', height: '100%', borderRadius: '12px',
  boxShadow: '0 1px 8px rgba(0,0,0,0.04)', border: '1px solid #e5e5ea',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
};
/** Nút "Tải thêm" dưới danh sách thẻ. */
export const sxNutTaiThem = { py: 1.5, color: 'primary.main', fontWeight: 'bold', textTransform: 'none', fontSize: '0.95rem', bgcolor: 'white', borderRadius: 3, border: '1px solid #e5e5ea' };

// ── BỘ LỌC NHANH ───────────────────────────────────────────────────────────────────────────────────────

/** Hàng bộ lọc nhanh dưới thanh tiêu đề: cuộn ngang, ẩn thanh cuộn. */
export const sxHangLocNhanh = {
  display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', px: 2, py: 1,
  bgcolor: NEN_DANH_SACH, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none',
};

/** Một chip của bộ lọc nhanh: đang chọn = nền xanh chữ trắng; còn lại = nền trắng viền mảnh. */
export function sxChipLocNhanh(dangChon, mau) {
  return {
    fontWeight: dangChon ? 'bold' : '500',
    bgcolor: dangChon ? MAU_NHAN : 'white',
    color: dangChon ? 'white' : (mau || 'text.primary'),
    border: dangChon ? 'none' : '1px solid #e5e5ea',
    '&:hover': { bgcolor: dangChon ? '#005bb5' : '#f4f4f5' },
  };
}
/** Nhãn tiêu đề đứng đầu hàng bộ lọc nhanh. */
export const sxNhanLocNhanh = { flexShrink: 0, fontWeight: 600, mr: 0.5 };
